import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Switch, Input, InputNumber, Divider, message } from 'antd';
import { Info, Plus, Trash2, Eye } from 'react-feather';
import { Typography, IconButton, Button, Tooltip, Link, SelectField } from 'ui-kit';
import _ from 'lodash';
import { EPriceType } from '../ProductBaseForm/constants';
import { getSplitReferencePrice } from '../utils';
import './ProductPayoutSettings.scss';

const DEFAULT_RECIPIENT_NAME = 'Store Owner';

const normalizeRecipientName = (name: string): string => {
  if (!name) {
    return DEFAULT_RECIPIENT_NAME;
  }
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();

  if (
    lower === 'storeowner' ||
    lower === 'store_owner' ||
    lower === 'store owner' ||
    lower === 'default store owner'
  ) {
    return DEFAULT_RECIPIENT_NAME;
  }

  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type PercentageRecipient = {
  recipient: string;
  percents: number;
};

const autoBalancePercentageSplits = (
  splits: PercentageRecipient[],
  changedIndex?: number,
): PercentageRecipient[] => {
  if (splits.length === 0) {
    return [];
  }
  if (splits.length === 1) {
    return [{ ...splits[0], percents: 100 }];
  }

  if (changedIndex === undefined) {
    const equalShare = Number((100 / splits.length).toFixed(2));
    let currentSum = 0;
    return splits.map((item, idx) => {
      if (idx === splits.length - 1) {
        return { ...item, percents: Number((100 - currentSum).toFixed(2)) };
      }
      currentSum += equalShare;
      return { ...item, percents: equalShare };
    });
  }

  const targetPercent = Math.min(100, Math.max(0, splits[changedIndex].percents || 0));
  splits[changedIndex].percents = targetPercent;

  const remainingPercent = 100 - targetPercent;
  const otherIndices = splits.map((_, i) => i).filter((i) => i !== changedIndex);
  const otherSum = otherIndices.reduce((sum, i) => sum + (splits[i].percents || 0), 0);

  let allocated = 0;
  otherIndices.forEach((i, idx) => {
    if (idx === otherIndices.length - 1) {
      splits[i].percents = Number((remainingPercent - allocated).toFixed(2));
    } else {
      const share =
        otherSum > 0
          ? (splits[i].percents / otherSum) * remainingPercent
          : remainingPercent / otherIndices.length;

      const rounded = Number(share.toFixed(2));
      splits[i].percents = rounded;
      allocated += rounded;
    }
  });

  return splits;
};
interface ProductPayoutSettingsProps {
  store?: any;
}
export default function ProductPayoutSettings({ store }: ProductPayoutSettingsProps) {
  const { control, getValues, setValue } = useFormContext();
  const { product } = getValues();

  const [availableNames, setAvailableNames] = useState<string[]>([DEFAULT_RECIPIENT_NAME]);
  const [newNameInput, setNewNameInput] = useState('');

  const isCustomPayoutSplit = useWatch({
    control,
    name: 'product.isCustomPayoutSplit',
  });

  const {
    fields: recipientAmountFields,
    append: appendAmountRecipient,
    remove: removeAmountRecipient,
  } = useFieldArray({
    control,
    name: 'product.amountSplits',
  });

  const { fields: recipientPercentageFields } = useFieldArray({
    control,
    name: 'product.percentageSplits',
  });

  useEffect(() => {
    const discovered = new Set<string>([DEFAULT_RECIPIENT_NAME]);

    const currentPercentageSplits = getValues('product.percentageSplits');
    if (Array.isArray(currentPercentageSplits)) {
      currentPercentageSplits.forEach((item: any) => {
        if (item?.recipient) {
          discovered.add(normalizeRecipientName(item.recipient));
        }
      });
    }

    const storeObj = store || product?.store || getValues('store');
    const storeSplits = storeObj?.customStoreSplits;

    if (storeSplits && typeof storeSplits === 'object' && !Array.isArray(storeSplits)) {
      Object.keys(storeSplits).forEach((rawName) => {
        if (rawName) {
          discovered.add(normalizeRecipientName(rawName));
        }
      });
    }

    setAvailableNames(Array.from(discovered));
  }, [getValues, store, product, isCustomPayoutSplit]);

  useEffect(() => {
    if (recipientAmountFields.length === 0) {
      appendAmountRecipient({ recipient: '', amount: '' });
    }
  }, []);

  const handleNewAmountRecipient = () => {
    appendAmountRecipient({
      recipient: '',
      amount: '',
    });
  };

  const handleNewPercentageRecipient = () => {
    const currentSplits = getValues('product.percentageSplits') || [];
    const updated = autoBalancePercentageSplits([
      ...currentSplits,
      { recipient: DEFAULT_RECIPIENT_NAME, percents: 0 },
    ]);
    setValue('product.percentageSplits', updated, { shouldDirty: true, shouldTouch: true });
  };

  const handleRemovePercentageRecipient = (indexToRemove: number) => {
    const currentSplits = getValues('product.percentageSplits') || [];
    if (currentSplits.length <= 1) {
      message.warning('You must have at least one recipient.');
      return;
    }
    const filtered = currentSplits.filter((_: any, idx: number) => idx !== indexToRemove);
    const updated = autoBalancePercentageSplits(filtered);
    setValue('product.percentageSplits', updated, { shouldDirty: true, shouldTouch: true });
  };

  const handlePercentageChange = (recipientIndex: number, newValue: number) => {
    const currentSplits = [...(getValues('product.percentageSplits') || [])];
    currentSplits[recipientIndex].percents = isNaN(newValue) ? 0 : newValue;

    const updated = autoBalancePercentageSplits(currentSplits, recipientIndex);
    setValue('product.percentageSplits', updated, { shouldDirty: true, shouldTouch: true });
  };

  const handleAddNewNameOption = (recipientIndex: number) => {
    const trimmed = newNameInput.trim();
    if (!trimmed) {
      return;
    }

    const normalized = normalizeRecipientName(trimmed);
    setAvailableNames((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));

    setValue(`product.percentageSplits.${recipientIndex}.recipient`, normalized, {
      shouldDirty: true,
      shouldTouch: true,
    });

    setNewNameInput('');
  };

  const handleDeleteNameOption = (nameToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (nameToDelete === DEFAULT_RECIPIENT_NAME) {
      message.warning('Cannot delete default recipient Store Owner.');
      return;
    }

    setAvailableNames((prev) => prev.filter((name) => name !== nameToDelete));

    const currentSplits = getValues('product.percentageSplits') || [];
    currentSplits.forEach((item: any, idx: number) => {
      if (item.recipient === nameToDelete) {
        setValue(`product.percentageSplits.${idx}.recipient`, DEFAULT_RECIPIENT_NAME, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
  };

  const handleToggleChange = (targetField: string) => {
    const currentCustom = getValues('product.isCustomPayoutSplit') || false;
    const currentDefault = getValues('product.isDefaultPayoutSplit') ?? true;

    if (targetField === 'default') {
      setValue('product.isDefaultPayoutSplit', !currentDefault);
      setValue('product.isCustomPayoutSplit', currentDefault);
    } else {
      const nextCustom = !currentCustom;
      setValue('product.isCustomPayoutSplit', nextCustom);
      setValue('product.isDefaultPayoutSplit', !nextCustom);

      if (nextCustom) {
        const currentSplits = getValues('product.percentageSplits');
        if (!currentSplits || currentSplits.length === 0) {
          setValue('product.percentageSplits', [
            { recipient: DEFAULT_RECIPIENT_NAME, percents: 100 },
          ]);
        }
      }
    }
  };

  const watchedAmountSplits = useWatch({
    control,
    name: 'product.amountSplits',
  });

  const watchedPercentageSplits = useWatch({
    control,
    name: 'product.percentageSplits',
  });

  const amountSplits =
    watchedAmountSplits?.length > 0 ? watchedAmountSplits : product?.amountSplits || [];

  const percentageSplits =
    watchedPercentageSplits?.length > 0 ? watchedPercentageSplits : product?.percentageSplits || [];

  const totalRecipientAmount = _.sumBy(
    amountSplits,
    (item: { amount?: string | number }) => Number(item?.amount) || 0,
  );

  const totalRecipientPercentage = _.sumBy(
    percentageSplits,
    (item: { percents?: string | number }) => Number(item?.percents) || 0,
  );

  const referencePrice = getSplitReferencePrice(product);
  const hasPriceOptions = product?.priceType === EPriceType.MULTIPLE;

  const isWholesaleCostValid =
    Number(totalRecipientAmount.toFixed(2)) <= Number(referencePrice.toFixed(2));
  const isPercentageValid = Math.abs(totalRecipientPercentage - 100) < 0.01;

  const profitToSplit = Math.max(0, referencePrice - totalRecipientAmount);

  const totalProfitAllocated = percentageSplits.reduce((acc, item) => {
    const percent = Number(item.percents || 0);
    return acc + (profitToSplit * percent) / 100;
  }, 0);

  const totalAllocated = totalRecipientAmount + totalProfitAllocated;

  useEffect(() => {
    if (isCustomPayoutSplit && totalRecipientPercentage > 100) {
      message.error(
        `Total profit split cannot exceed 100% (Currently ${totalRecipientPercentage}%).`,
        5,
      );
    }
  }, [isCustomPayoutSplit, totalRecipientPercentage]);

  const dropdownOptions = availableNames.map((name) => ({
    label: name,
    value: name,
  }));

  return (
    <div className="payout-modal">
      <div className="payout-modal__content">
        <div className="payout-modal__toggle-box" style={{ marginBottom: 20 }}>
          <Controller
            control={control}
            name="product.isDefaultPayoutSplit"
            render={({ field: { value } }) => (
              <div
                className="toggle-group"
                style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
              >
                <Switch checked={Boolean(value)} onChange={() => handleToggleChange('default')} />
                <div className="toggle-label-content">
                  <label
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => handleToggleChange('default')}
                  >
                    Use {'  '}
                    <Link
                      to="/overview/payouts/?section=payout-methods"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#047857', textDecoration: 'underline' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      store default payout split
                    </Link>
                  </label>
                </div>
              </div>
            )}
          />
        </div>

        <div className="payout-modal__toggle-box" style={{ marginBottom: 20 }}>
          <Controller
            control={control}
            name="product.isCustomPayoutSplit"
            render={({ field: { value } }) => (
              <div
                className="toggle-group"
                style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
              >
                <Switch checked={Boolean(value)} onChange={() => handleToggleChange('custom')} />
                <div className="toggle-label-content">
                  <label
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => handleToggleChange('custom')}
                  >
                    Set a custom split for this product only
                  </label>
                </div>
              </div>
            )}
          />
        </div>

        {isCustomPayoutSplit && (
          <div className="payout-modal__grid">
            <div className="payout-modal__form">
              <div className="payout-modal__banner">
                <Tooltip title="">
                  <IconButton style={{ padding: 0 }}>
                    <Info size={16} />
                  </IconButton>
                </Tooltip>
                <div className="banner-text">
                  <strong>This split is for reporting and tracking only.</strong>
                  <span>All payouts will go to the store&apos;s primary payout method.</span>
                </div>
              </div>

              <div className="payout-modal__section">
                <div className="section-header">
                  <div className="title-group">
                    <span className="badge">1</span>
                    <div>
                      <Typography variant="h6">Wholesale cost</Typography>
                      <p>
                        Enter any fixed costs (e.g., vendor or service provider) associated with
                        this product.
                      </p>
                    </div>
                  </div>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={handleNewAmountRecipient}
                    className="add-btn"
                  >
                    Add recipient
                  </Button>
                </div>

                {hasPriceOptions && (
                  <div className="price-options-warning">
                    <Info size={16} />
                    <span>
                      This product has more than one price option. The same wholesale cost applies
                      to every option, and it cannot be more than the lowest price.
                    </span>
                  </div>
                )}

                {recipientAmountFields.length > 0 && (
                  <div className="list-labels">
                    <span className="label-recipient">Recipient</span>
                    <span className="label-value">Amount</span>
                  </div>
                )}

                {!isWholesaleCostValid && (
                  <div style={{ color: '#ff4d4f', marginTop: 8, fontSize: 13, fontWeight: 500 }}>
                    Wholesale cost (${totalRecipientAmount.toFixed(2)}) cannot exceed{' '}
                    {hasPriceOptions ? 'the lowest price' : 'product price'} ($
                    {referencePrice.toFixed(2)}).
                  </div>
                )}

                {recipientAmountFields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: 12 }}>
                    <div className="row-input">
                      <Controller
                        control={control}
                        name={`product.amountSplits.${index}.recipient`}
                        render={({
                          field: { ref: _ref, ...fieldProps },
                          fieldState: { error },
                        }) => (
                          <Input
                            {...fieldProps}
                            placeholder="e.g. Cleaner"
                            className={`text-input ${
                              !_.isUndefined(error) ? 'ant-input-status-error' : ''
                            }`}
                          />
                        )}
                      />

                      <div className="value-input-wrapper">
                        <span>$</span>
                        <Controller
                          control={control}
                          name={`product.amountSplits.${index}.amount`}
                          render={({
                            field: { ref: _ref, ...fieldProps },
                            fieldState: { error },
                          }) => (
                            <InputNumber
                              {...fieldProps}
                              placeholder="0"
                              bordered={false}
                              className={!_.isUndefined(error) ? 'ant-input-status-error' : ''}
                            />
                          )}
                        />
                      </div>

                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => removeAmountRecipient(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="total-bar">
                  <div className="label-group">
                    <Typography variant="h6">Total wholesale cost</Typography>
                  </div>
                  <span
                    className={`total-value ${!isWholesaleCostValid ? 'error-text' : 'highlight'}`}
                    style={{ color: !isWholesaleCostValid ? '#ff4d4f' : undefined }}
                  >
                    ${totalRecipientAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="payout-modal__section">
                <div className="section-header">
                  <div className="title-group">
                    <span className="badge">2</span>
                    <div>
                      <Typography variant="h6">Profit split</Typography>
                      <p>The remaining profit is split between recipients below.</p>
                    </div>
                  </div>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={handleNewPercentageRecipient}
                    className="add-btn"
                  >
                    Add recipient
                  </Button>
                </div>

                {recipientPercentageFields.length > 0 && (
                  <div className="list-labels">
                    <span className="label-recipient">Recipient</span>
                    <span className="label-value">Percentage</span>
                  </div>
                )}

                {!isPercentageValid && (
                  <div
                    style={{
                      color: '#ff4d4f',
                      margin: '8px 0 12px 0',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    Total profit split must equal 100% (Currently {totalRecipientPercentage}%).
                  </div>
                )}

                {recipientPercentageFields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: 12 }}>
                    <div className="row-input">
                      <Controller
                        control={control}
                        name={`product.percentageSplits.${index}.recipient`}
                        render={({ field: { onChange, value, ...fieldProps } }) => {
                          const normalizedVal = normalizeRecipientName(value);

                          return (
                            <div className="select-wrapper">
                              <SelectField
                                {...fieldProps}
                                value={normalizedVal || undefined}
                                onChange={(val: any) => onChange(val)}
                                className="PayoutMethods_recipient-select"
                                options={dropdownOptions}
                                placeholder="Select or enter recipient"
                                onDropdownVisibleChange={(open: boolean) => {
                                  if (!open) {
                                    handleAddNewNameOption(index);
                                  }
                                }}
                                dropdownRender={() => (
                                  <div className="PayoutMethods_dropdown-menu">
                                    <div className="PayoutMethods_dropdown-options">
                                      {availableNames.length === 0 ? (
                                        <div className="PayoutMethods_dropdown-empty">
                                          No recipients added yet
                                        </div>
                                      ) : (
                                        availableNames.map((name) => (
                                          <div
                                            key={name}
                                            className={`PayoutMethods_dropdown-item ${
                                              normalizedVal === name ? 'selected' : ''
                                            }`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                              setNewNameInput('');
                                              onChange(name);
                                            }}
                                          >
                                            <span>{name}</span>
                                            {name !== DEFAULT_RECIPIENT_NAME && (
                                              <button
                                                type="button"
                                                className="PayoutMethods_dropdown-delete-btn"
                                                onClick={(e) => handleDeleteNameOption(name, e)}
                                                title="Delete recipient"
                                              >
                                                <Trash2 size={13} />
                                              </button>
                                            )}
                                          </div>
                                        ))
                                      )}
                                    </div>

                                    <Divider style={{ margin: '8px 0' }} />

                                    <div
                                      style={{
                                        display: 'flex',
                                        padding: '0 8px 4px',
                                        gap: '8px',
                                      }}
                                    >
                                      <Input
                                        placeholder="Enter custom name..."
                                        value={newNameInput}
                                        onChange={(e) => setNewNameInput(e.target.value)}
                                        onBlur={() => handleAddNewNameOption(index)}
                                        onKeyDown={(e) => {
                                          e.stopPropagation();
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            (e.target as HTMLElement).blur();
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="contained"
                                        aria-label="Add recipient name"
                                        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                                        onClick={() => handleAddNewNameOption(index)}
                                        style={{ padding: '4px 8px', minWidth: 'auto' }}
                                      >
                                        <Plus size={16} />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              />
                            </div>
                          );
                        }}
                      />

                      <div className="value-input-wrapper">
                        <Controller
                          control={control}
                          name={`product.percentageSplits.${index}.percents`}
                          render={({ field }) => (
                            <InputNumber
                              {...field}
                              placeholder="0"
                              bordered={false}
                              min={0}
                              max={100}
                              onChange={(val) =>
                                handlePercentageChange(index, typeof val === 'number' ? val : 0)
                              }
                            />
                          )}
                        />
                        <span>%</span>
                      </div>

                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleRemovePercentageRecipient(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="total-bar">
                  <div className="label-group">
                    <Typography variant="h6">Total profit split</Typography>
                  </div>
                  <span
                    className={`total-value ${!isPercentageValid ? 'error-text' : 'highlight'}`}
                    style={{ color: !isPercentageValid ? '#ff4d4f' : undefined }}
                  >
                    {totalRecipientPercentage}%
                  </span>
                </div>
              </div>
            </div>

            {/* Right - Preview */}
            <div className="payout-modal__preview">
              <div>
                <div className="preview-header">
                  <Typography variant="h6">Total</Typography>
                  <Eye size={16} />
                </div>

                <div className="preview-row bold">
                  <Typography variant="body1" className="label">
                    {hasPriceOptions ? 'Lowest price' : 'Product price'}
                  </Typography>
                  <Typography variant="body2">${referencePrice.toFixed(2)}</Typography>
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-group">
                  <div className="preview-section-title">Wholesale cost</div>

                  {amountSplits.map((item, index) => (
                    <div key={index} className="preview-row">
                      <span className="label">{item.recipient || ''}</span>
                      <span className="value">${Number(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-group">
                  <div className="preview-section-title">Profit split</div>

                  {percentageSplits.map((item, index) => {
                    const percent = Number(item.percents || 0);
                    const calculatedAmount = (profitToSplit * percent) / 100;
                    const recipientDisplayName = normalizeRecipientName(item.recipient);

                    return (
                      <div key={index} className="preview-row">
                        <span className="label">
                          {recipientDisplayName} ({percent || 0}%)
                        </span>
                        <span className="value">${calculatedAmount.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-total-box">
                  <Typography variant="body1">Total allocated</Typography>
                  <span
                    className="amount"
                    style={{
                      color: !isPercentageValid || !isWholesaleCostValid ? '#ff4d4f' : undefined,
                    }}
                  >
                    ${totalAllocated.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="preview-footer-note">
                <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography variant="h6" style={{ fontSize: 11 }}>
                  For reporting and tracking only.
                  <br />
                  All payouts go to your primary payout method.
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
